import { Fragment, useEffect, useMemo, useState } from 'react';
import { KeenIcon } from '@/components';
import { I18N_LANGUAGES } from '@/i18n';
import { usePermissions } from '@/hooks/usePermissions';
import { useSnackbar } from 'notistack';
import {
  useGenerateHelpdeskFAQDraftsMutation,
  usePublishHelpdeskFAQTranslationMutation,
  useRollbackHelpdeskFAQTranslationMutation,
  useUpdateHelpdeskFAQTranslationDraftMutation,
} from '@/services/HelpdeskFAQService';
import {
  HelpdeskFAQ,
  HelpdeskFAQTranslationVariant,
} from '@/types/cms.types';
import moment from 'moment';

interface FAQTranslationWorkflowSectionProps {
  faq: HelpdeskFAQ;
  onRefresh: () => Promise<unknown>;
}

type DraftEditorState = {
  title: string;
  body: string;
  isOpen: boolean;
};

const statusClasses: Record<HelpdeskFAQTranslationVariant['status'], string> = {
  missing: 'badge badge-outline',
  draft: 'badge badge-warning badge-outline',
  published: 'badge badge-success badge-outline',
};

const getLanguageLabel = (languageCode: string) =>
  I18N_LANGUAGES.find((language) => language.code === languageCode)?.label ||
  languageCode.toUpperCase();

const getLanguageFlag = (languageCode: string) =>
  I18N_LANGUAGES.find((language) => language.code === languageCode)?.flag ||
  '/media/flags/globe.svg';

const getEditorSeed = (variant?: HelpdeskFAQTranslationVariant | null) => ({
  title: variant?.draft?.title || variant?.published?.title || '',
  body: variant?.draft?.body || variant?.published?.body || '',
});

const FAQTranslationWorkflowSection = ({
  faq,
  onRefresh,
}: FAQTranslationWorkflowSectionProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();
  const hasDraftPermission = hasPermission('faq_management_draft');
  const hasPublishPermission = hasPermission('faq_management_publish');
  const hasRollbackPermission = hasPermission('faq_management_rollback');

  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [generationMode, setGenerationMode] = useState<
    'overwrite_draft' | 'create_new_draft_version'
  >('overwrite_draft');
  const [draftEditors, setDraftEditors] = useState<
    Record<string, DraftEditorState>
  >({});

  const [generateDrafts, { isLoading: isGeneratingDrafts }] =
    useGenerateHelpdeskFAQDraftsMutation();
  const [saveDraft, { isLoading: isSavingDraft }] =
    useUpdateHelpdeskFAQTranslationDraftMutation();
  const [publishTranslation, { isLoading: isPublishingTranslation }] =
    usePublishHelpdeskFAQTranslationMutation();
  const [rollbackTranslation, { isLoading: isRollingBackTranslation }] =
    useRollbackHelpdeskFAQTranslationMutation();

    console.log(faq.translation_variants,"variaaaaaaaaaa");
    
  const variants = faq.translation_variants || [];
  const sourceVariant = variants.find((variant) => variant.language === faq.language);
  const targetLanguages = useMemo(
    () => I18N_LANGUAGES.filter((language) => language.code !== faq.language),
    [faq.language]
  );

  const variantMap = useMemo(
    () =>
      variants.reduce<Record<string, HelpdeskFAQTranslationVariant>>(
        (acc, variant) => {
          acc[variant.language] = variant;
          return acc;
        },
        {}
      ),
    [variants]
  );

  useEffect(() => {
    setDraftEditors((previous) =>
      targetLanguages.reduce<Record<string, DraftEditorState>>(
        (acc, language) => {
          const variant = variantMap[language.code];
          const currentEditor = previous[language.code];
          const seed = getEditorSeed(variant);

          acc[language.code] = currentEditor
            ? {
                ...currentEditor,
                title: currentEditor.isOpen ? currentEditor.title : seed.title,
                body: currentEditor.isOpen ? currentEditor.body : seed.body,
              }
            : {
                ...seed,
                isOpen: variant?.status === 'draft',
              };

          return acc;
        },
        {}
      )
    );
  }, [targetLanguages, variantMap]);

  const handleLanguageSelection = (languageCode: string) => {
    setSelectedLanguages((previous) =>
      previous.includes(languageCode)
        ? previous.filter((language) => language !== languageCode)
        : [...previous, languageCode]
    );
  };

  const handleBulkGenerate = async () => {
    if (!selectedLanguages.length) return;

    try {
      await generateDrafts({
        id: faq.id,
        target_languages: selectedLanguages,
        source_language: faq.language,
        mode: generationMode,
      }).unwrap();
      enqueueSnackbar('Translation drafts generated successfully', {
        variant: 'success',
      });
      setSelectedLanguages([]);
      await onRefresh();
    } catch {
      enqueueSnackbar('Failed to generate translation drafts', {
        variant: 'error',
      });
    }
  };

  const toggleEditor = (languageCode: string) => {
    const variant = variantMap[languageCode];
    const seed = getEditorSeed(variant);

    setDraftEditors((previous) => ({
      ...previous,
      [languageCode]: previous[languageCode]
        ? {
            ...previous[languageCode],
            isOpen: !previous[languageCode].isOpen,
            title: previous[languageCode].isOpen
              ? seed.title
              : previous[languageCode].title,
            body: previous[languageCode].isOpen
              ? seed.body
              : previous[languageCode].body,
          }
        : {
            ...seed,
            isOpen: true,
          },
    }));
  };

  const updateEditorField = (
    languageCode: string,
    field: 'title' | 'body',
    value: string
  ) => {
    setDraftEditors((previous) => ({
      ...previous,
      [languageCode]: {
        ...(previous[languageCode] || { title: '', body: '', isOpen: true }),
        [field]: value,
      },
    }));
  };

  const handleSaveDraft = async (languageCode: string) => {
    const editor = draftEditors[languageCode];
    if (!editor?.title.trim() || !editor?.body.trim()) {
      enqueueSnackbar('Draft title and body are required', {
        variant: 'warning',
      });
      return;
    }

    try {
      await saveDraft({
        id: faq.id,
        language: languageCode,
        title: editor.title,
        body: editor.body,
      }).unwrap();
      enqueueSnackbar('Draft saved successfully', { variant: 'success' });
      await onRefresh();
    } catch {
      enqueueSnackbar('Failed to save translation draft', {
        variant: 'error',
      });
    }
  };

  const handlePublish = async (languageCode: string) => {
    try {
      await publishTranslation({
        id: faq.id,
        language: languageCode,
      }).unwrap();
      enqueueSnackbar('Translation published successfully', {
        variant: 'success',
      });
      await onRefresh();
    } catch {
      enqueueSnackbar('Failed to publish translation', {
        variant: 'error',
      });
    }
  };

  const handleRollback = async (languageCode: string) => {
    try {
      await rollbackTranslation({
        id: faq.id,
        language: languageCode,
      }).unwrap();
      enqueueSnackbar('Previous published version restored', {
        variant: 'success',
      });
      await onRefresh();
    } catch {
      enqueueSnackbar('Failed to restore previous published version', {
        variant: 'error',
      });
    }
  };

  const isAnyMutationRunning =
    isGeneratingDrafts ||
    isSavingDraft ||
    isPublishingTranslation ||
    isRollingBackTranslation;

  return (
    <div className="card mt-6">
      <div className="card-header">
        <div>
          <h3 className="card-title">Translation Workflow</h3>
          <p className="text-sm text-gray-600 mt-1">
            Auto-generated FAQ translations stay as drafts until an editor
            reviews and publishes them.
          </p>
        </div>
      </div>
      <div className="card-body space-y-6">
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-gray-800">
                Source language
              </p>
              <p className="text-sm text-gray-600">
                {getLanguageLabel(faq.language)} is the currently published
                source for this FAQ.
              </p>
              {sourceVariant?.last_updated_at && (
                <p className="text-xs text-gray-500 mt-1">
                  Last updated{' '}
                  {moment(sourceVariant.last_updated_at).format(
                    'DD MMM YYYY, hh:mm A'
                  )}
                </p>
              )}
            </div>
            <span className="badge badge-success badge-outline">Published</span>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 p-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h4 className="text-base font-semibold text-gray-800">
                Generate Draft Translations
              </h4>
              <p className="text-sm text-gray-600">
                Select one or more target languages, then choose how existing
                drafts should be handled.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={generationMode}
                onChange={(event) =>
                  setGenerationMode(
                    event.target.value as
                      | 'overwrite_draft'
                      | 'create_new_draft_version'
                  )
                }
                className="select select-sm select-bordered"
                disabled={!hasDraftPermission || isAnyMutationRunning}
              >
                <option value="overwrite_draft">Overwrite draft</option>
                <option value="create_new_draft_version">
                  Create new draft version
                </option>
              </select>
              <button
                className="btn btn-sm btn-primary"
                onClick={handleBulkGenerate}
                disabled={
                  !hasDraftPermission ||
                  !selectedLanguages.length ||
                  isAnyMutationRunning
                }
              >
                {isGeneratingDrafts ? 'Generating...' : 'Generate Drafts'}
              </button>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            {targetLanguages.map((language) => {
              const variant = variantMap[language.code];
              const isSelected = selectedLanguages.includes(language.code);

              return (
                <label
                  key={language.code}
                  className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm mt-0.5"
                    checked={isSelected}
                    onChange={() => handleLanguageSelection(language.code)}
                    disabled={!hasDraftPermission || isAnyMutationRunning}
                  />
                  <img
                    src={language.flag}
                    alt={language.label}
                    className="w-5 h-5 rounded-sm mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-800">
                        {language.label}
                      </span>
                      <span className={statusClasses[variant?.status || 'missing']}>
                        {(variant?.status || 'missing').toUpperCase()}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {variant?.last_updated_label
                        ? `Last updated by ${variant.last_updated_label}`
                        : 'No translation workflow activity yet'}
                    </p>
                  </div>
                </label>
              );
            })}
          </div>

          {!hasDraftPermission && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
              You can view translation states, but generating and editing drafts
              requires the FAQ draft permission.
            </p>
          )}
        </div>

        <div className="space-y-4">
          {targetLanguages.map((language) => {
            const variant = variantMap[language.code];
            const editorState = draftEditors[language.code] || {
              title: '',
              body: '',
              isOpen: false,
            };

            return (
              <div
                key={language.code}
                className="rounded-xl border border-gray-200 overflow-hidden"
              >
                <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <img
                      src={getLanguageFlag(language.code)}
                      alt={language.label}
                      className="w-6 h-6 rounded-sm mt-0.5"
                    />
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-gray-800">
                          {getLanguageLabel(language.code)}
                        </span>
                        <span
                          className={
                            statusClasses[variant?.status || 'missing']
                          }
                        >
                          {(variant?.status || 'missing').toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {variant?.last_updated_label
                          ? `${variant.last_updated_label} • ${
                              variant.last_updated_at
                                ? moment(variant.last_updated_at).format(
                                    'DD MMM YYYY, hh:mm A'
                                  )
                                : 'No timestamp'
                            }`
                          : 'Missing translation'}
                      </p>
                      {variant?.status === 'draft' &&
                        variant.has_published_version && (
                          <p className="text-xs text-amber-700 mt-1">
                            A published version is still live until this draft is
                            published.
                          </p>
                        )}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {hasDraftPermission && (
                      <button
                        className="btn btn-sm btn-light"
                        onClick={() => toggleEditor(language.code)}
                        disabled={isAnyMutationRunning}
                      >
                        <KeenIcon
                          icon={editorState.isOpen ? 'eye' : 'pencil'}
                          className="mr-2"
                        />
                        {editorState.isOpen ? 'Preview' : 'Edit Draft'}
                      </button>
                    )}
                    {hasPublishPermission && variant?.draft && (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handlePublish(language.code)}
                        disabled={isAnyMutationRunning}
                      >
                        Publish
                      </button>
                    )}
                    {hasRollbackPermission && variant?.can_rollback && (
                      <button
                        className="btn btn-sm btn-light"
                        onClick={() => handleRollback(language.code)}
                        disabled={isAnyMutationRunning}
                      >
                        Rollback
                      </button>
                    )}
                  </div>
                </div>

                <div className="p-4 space-y-4">
                  {editorState.isOpen ? (
                    <Fragment>
                      <div>
                        <label className="form-label">Draft Title</label>
                        <input
                          type="text"
                          value={editorState.title}
                          onChange={(event) =>
                            updateEditorField(
                              language.code,
                              'title',
                              event.target.value
                            )
                          }
                          className="input input-bordered w-full"
                          placeholder={`Enter ${getLanguageLabel(
                            language.code
                          )} FAQ title`}
                          disabled={!hasDraftPermission || isAnyMutationRunning}
                        />
                      </div>
                      <div>
                        <label className="form-label">Draft Body</label>
                        <textarea
                          value={editorState.body}
                          onChange={(event) =>
                            updateEditorField(
                              language.code,
                              'body',
                              event.target.value
                            )
                          }
                          className="textarea textarea-bordered w-full h-48"
                          placeholder={`Enter ${getLanguageLabel(
                            language.code
                          )} FAQ body`}
                          disabled={!hasDraftPermission || isAnyMutationRunning}
                        />
                      </div>
                      {hasDraftPermission && (
                        <div className="flex justify-end">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleSaveDraft(language.code)}
                            disabled={isAnyMutationRunning}
                          >
                            Save Draft
                          </button>
                        </div>
                      )}
                    </Fragment>
                  ) : (
                    <Fragment>
                      <div>
                        <p className="form-label">Current Title</p>
                        <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-800 min-h-[42px]">
                          {variant?.draft?.title ||
                            variant?.published?.title ||
                            'No translation available'}
                        </div>
                      </div>
                      <div>
                        <p className="form-label">Current Body</p>
                        <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-700 whitespace-pre-wrap min-h-[120px]">
                          {variant?.draft?.body ||
                            variant?.published?.body ||
                            'No translation available'}
                        </div>
                      </div>
                    </Fragment>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FAQTranslationWorkflowSection;
